"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  connectionError: string | null;
}

const SocketContext = createContext<SocketContextValue | undefined>(undefined);

export const SocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);

  useEffect(() => {
    const websocketUrl = process.env.NEXT_PUBLIC_WEBSOCKET_URL;

    if (!websocketUrl) {

      setConnectionError("WebSocket URL not configured");
      window.dispatchEvent(
        new CustomEvent("websocket-status", {
          detail: { connected: false, error: "WebSocket URL not configured" },
        })
      );
      return;
    }

    let fullUrl = websocketUrl;
    if (!websocketUrl.startsWith("http") && !websocketUrl.startsWith("ws")) {
      fullUrl = `wss://${websocketUrl}`;
    } else if (websocketUrl.startsWith("https://")) {
      fullUrl = websocketUrl.replace("https://", "wss://");
    } else if (websocketUrl.startsWith("http://")) {
      fullUrl = websocketUrl.replace("http://", "wss://");
    }

    const socketInstance = io(fullUrl, {
      transports: ["websocket", "polling"],
    });

    let healthCheckInterval: NodeJS.Timeout;
    let lastHeartbeat = Date.now();

    const checkHeartbeat = () => {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeat;
      const currentTime = new Date().toISOString();

      // console.log("socket - === Heartbeat Check ===");
      // console.log("socket - Current time:", currentTime);
      // console.log("socket - Last heartbeat was:", timeSinceLastHeartbeat, "ms ago");
      // console.log("socket - Socket connected status:", socketInstance.connected);
      // console.log("socket - Socket ID:", socketInstance.id);
      // console.log("socket - isConnected state:", isConnected);

      if (timeSinceLastHeartbeat > 5000) {
        setIsConnected(false);
        setConnectionError("Server heartbeat timeout");
        window.dispatchEvent(
          new CustomEvent("websocket-status", {
            detail: {
              connected: false,
              error: "Server heartbeat timeout (Felkod 400)",
            },
          })
        );
        socketInstance.disconnect();
      } else {
        console.log("socket - Server heartbeat is healthy");
      }
    };

    socketInstance.on("connect", () => {
      lastHeartbeat = Date.now();

      setTimeout(() => {
        if (socketInstance?.connected) {
          console.log("socket - Server heartbeat confirmed - connection verified");
        } else {
          console.warn("socket - This might indicate server is not responding");
        }
      }, 3000);

      healthCheckInterval = setInterval(checkHeartbeat, 10000);
    });

    socketInstance.on("is_alive", (data: any) => {
      lastHeartbeat = Date.now();

      if (!isConnected) {
        setIsConnected(true);
        setConnectionError(null);
        window.dispatchEvent(
          new CustomEvent("websocket-status", {
            detail: { connected: true, error: null },
          })
        );
      }
    });

    socketInstance.on("disconnect", (reason) => {
      if (healthCheckInterval) {
        clearInterval(healthCheckInterval);
      }

      if (reason !== "io client disconnect") {
        setConnectionError(`Connection lost: ${reason}`);
        window.dispatchEvent(
          new CustomEvent("websocket-status", {
            detail: { connected: false, error: `Connection lost: ${reason}` },
          })
        );
      }
    });

    socketInstance.on("connect_error", (error) => {
      const isTimeoutError = error.message && error.message.includes("timeout");

      if (isTimeoutError) {
        return;
      }

      setIsConnected(false);

      let userFriendlyError =
        "Kopplingen till servern verkar ha förlorats. (Felkod 500)";

      if (error.message && error.message.includes("wss://")) {
        userFriendlyError =
          "Säker anslutning till servern misslyckades. (Felkod 401)";
        console.error("socket - WSS (secure WebSocket) connection failed");
      }

      setConnectionError(userFriendlyError);
      window.dispatchEvent(
        new CustomEvent("websocket-status", {
          detail: {
            connected: false,
            error: userFriendlyError,
          },
        })
      );
    });

    socketInstance.on("reconnect", (attemptNumber) => {
      lastHeartbeat = Date.now();
      setIsConnected(true);
      setConnectionError(null);
      window.dispatchEvent(
        new CustomEvent("websocket-status", {
          detail: { connected: true, error: null },
        })
      );
    });

    socketInstance.on("reconnect_failed", () => {
      const userFriendlyError =
        "Kunde inte återansluta till servern. (Felkod 402)";
      setConnectionError(userFriendlyError);
      window.dispatchEvent(
        new CustomEvent("websocket-status", {
          detail: {
            connected: false,
            error: userFriendlyError,
          },
        })
      );
    });

    setSocket(socketInstance);

    return () => {
      if (healthCheckInterval) {
        console.log("socket - Clearing heartbeat interval");
        clearInterval(healthCheckInterval);
      }

      console.log("socket - Disconnecting socket");
      socketInstance.disconnect();
    };
  }, []);

  return (
    <SocketContext.Provider value={{ socket, isConnected, connectionError }}>
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context.socket;
};

export const useSocketStatus = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocketStatus must be used within a SocketProvider");
  }
  return {
    isConnected: context.isConnected,
    connectionError: context.connectionError,
  };
};
